import ky, { type KyInstance } from "ky";

import type { BackofficeContext } from "../../framework/context/types";
import { mapKyHttpError } from "../ky-error";
import { prepareQueryParams, toSearchParams } from "../query";
import { parseHttpBody, parseRecordList, type ApiResponse } from "../response";

export interface ResultsApi {
  getFilters: (filters: Record<string, unknown>) => Promise<Record<string, unknown>[]>;
}

export type ResultsApiDeps = Pick<BackofficeContext, "read" | "auth">;

export function createResultsApi(deps: ResultsApiDeps, http?: KyInstance): ResultsApi {
  const { read, auth } = deps;

  const client =
    http ??
    ky.create({
      prefix: `${import.meta.env.NXG_SPORTS_AIO_CORE}/tenants/${read.tenantId()}/instances/${read.instance()}/languages/${read.language()}`,
      retry: 0,
      throwHttpErrors: true,
      headers: { Accept: "application/json" },
      hooks: {
        beforeRequest: [
          ({ request }) => {
            request.headers.set("Authorization", `Bearer ${auth.getAccessToken()}`);
          },
        ],
        beforeError: [({ error }) => mapKyHttpError(error)],
      },
    });

  return {
    getFilters: async (filters) => {
      const body = parseHttpBody(
        await client
          .get("results/filters", {
            searchParams: toSearchParams({
              ...prepareQueryParams(filters),
              isReadAfterWrite: filters.isReadAfterWrite || false,
              search: filters.searchParameter ?? filters.search,
            }),
          })
          .text(),
      ) as ApiResponse;
      return parseRecordList(body.data);
    },
  };
}
